package team.platforma.multiplatform_material_extension.ui.buttons

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonColors
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ButtonElevation
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp
import team.platforma.multiplatform_material_extension.correction
import team.platforma.multiplatform_material_extension.trackWebKeyboardHeight
import team.platforma.multiplatform_material_extension.ui.buttons.utils.initButtonKeyGrabber
import team.platforma.multiplatform_material_extension.ui.buttons.utils.observeKeyBoardFab
import team.platforma.multiplatform_material_extension.ui.core.global_style.takeGlobalFabButtonColors
import team.platforma.multiplatform_material_extension.ui.core.global_style.takeGlobalFabShape
import team.platforma.multiplatform_material_extension.util.KeyGrabber

@Composable
fun ExtraFAB(
    expanded: Boolean = false,
    areaModifier: Modifier = Modifier,
    fabModifier: Modifier = Modifier,
    expandStrategy: ExpandStrategy = ExpandStrategy.WrapContent,
    iconGravity: IconGravity = IconGravity.End,
    text: @Composable (() -> Unit)? = null,
    icon: @Composable (() -> Unit),
    enabled: Boolean = true,
    shape: Shape? = null,
    colors: ButtonColors? = null,
    onClick: () -> Unit,
    elevation: ButtonElevation? = null,
    expandDuration: Int? = null,
    shrinkDuration: Int? = null,
    keyGrabber: KeyGrabber? = null,
    onClickKey: Key? = null
) {
    var expand by remember { mutableStateOf(false) }
    var fabEnabled by remember { mutableStateOf(true) }

    val density = LocalDensity.current

    LaunchedEffect(expanded) {
        expand = expanded
    }

    LaunchedEffect(enabled) {
        fabEnabled = enabled
    }

    var bottomPadding by remember { mutableStateOf(16) }

    observeKeyBoardFab {
        bottomPadding = it
    }



    var maxWidth by remember { mutableStateOf(0f) }


    val width by animateDpAsState(
        targetValue = if (expand) {
            if (text != null) {
                with(density) { maxWidth.toDp() }
            } else {
                56.dp
            }
        } else {
            56.dp
        },
        animationSpec = if (expand) {
            when (expandStrategy) {
                ExpandStrategy.MaxWidth -> tween(durationMillis = expandDuration ?: 200)
                ExpandStrategy.WrapContent -> tween(durationMillis = expandDuration ?: 60)
            }

        } else {
            tween(durationMillis = shrinkDuration ?: 120)
        }
    )

    val alpha by animateFloatAsState(
        targetValue = if (expand) 1f else 0f,
        animationSpec = if (expand) tween(durationMillis = 120) else tween(durationMillis = 60)
    )


    initButtonKeyGrabber(keyGrabber, onClickKey){
        if (fabEnabled) {
            onClick()
        }
    }

    if (expandStrategy == ExpandStrategy.MaxWidth) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .onGloballyPositioned { coordinates ->
                    if (text != null) {
                        maxWidth = coordinates.size.width.toFloat()
                    }

                }
        )
    }

    Box(
        modifier = areaModifier.then(
            Modifier.imePadding().padding(bottom = bottomPadding.dp)
        )
    ) {
        Button(
            modifier = fabModifier.then(
                Modifier.width(width).heightIn(min = 56.dp, max = 56.dp)
            ),
            onClick = { onClick() },
            contentPadding = PaddingValues(0.dp),
            enabled = fabEnabled,
            elevation = elevation ?: ButtonDefaults.buttonElevation(
                defaultElevation = 2.dp,
                pressedElevation = 0.dp,
                focusedElevation = 2.dp,
                hoveredElevation = 2.dp,
                disabledElevation = 0.dp
            ),
            shape = shape ?: takeGlobalFabShape(),
            colors = colors ?: takeGlobalFabButtonColors()
        ) {

            Box(
                modifier = Modifier,
                contentAlignment = when (iconGravity) {
                    IconGravity.End -> Alignment.CenterEnd
                    IconGravity.Start -> Alignment.CenterStart
                }
            ) {
                Column(
                    Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (text != null) {
                        Box(
                            modifier = Modifier
                                .onGloballyPositioned
                                { layoutCoordinates ->
                                    if (expandStrategy == ExpandStrategy.WrapContent) {
                                        maxWidth =
                                            layoutCoordinates.size.width.toFloat() + correction(56.dp)
                                    }

                                }.alpha(alpha)
                                .padding(
                                    start = when (expandStrategy) {
                                        ExpandStrategy.WrapContent -> {
                                            when (iconGravity) {
                                                IconGravity.Start -> 24.dp
                                                IconGravity.End -> 0.dp
                                            }
                                        }

                                        ExpandStrategy.MaxWidth -> {
                                            0.dp
                                        }
                                    },
                                    end = when (expandStrategy) {
                                        ExpandStrategy.WrapContent -> {
                                            when (iconGravity) {
                                                IconGravity.Start -> 0.dp
                                                IconGravity.End -> 24.dp
                                            }
                                        }

                                        ExpandStrategy.MaxWidth -> {
                                            0.dp
                                        }
                                    }
                                )
                        ) {
                            text()
                        }

                    }

                }



                Box(
                    modifier = when (iconGravity) {
                        IconGravity.End -> Modifier.size(48.dp).padding(end = 8.dp)
                        IconGravity.Start -> Modifier.size(48.dp).padding(start = 8.dp)
                    },
                    contentAlignment = Alignment.Center
                ) {
                    icon()
                }
            }
        }
    }
}


enum class ExpandStrategy {
    MaxWidth,
    WrapContent
}

enum class IconGravity {
    Start, End
}

